import { useMemo, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { movies } from "@/mocks/movies";
import { movieCategories, regions, decades } from "@/mocks/categories";
import { filterMovies, getCategoryBySlug, paginate, sortMovies, type SortKey } from "@/utils/catalog";
import SiteHeader from "@/components/feature/SiteHeader";
import SiteFooter from "@/components/feature/SiteFooter";
import MovieCard from "@/components/feature/MovieCard";
import Pagination from "@/components/feature/Pagination";
import usePageMeta from "@/hooks/usePageMeta";

const PAGE_SIZE = 8;

const sortOptions: { key: SortKey; label: string }[] = [
  { key: "latest", label: "按上映时间" },
  { key: "hot", label: "按热门程度" },
  { key: "rating", label: "按评分" },
];

export default function CategoryPage() {
  const { type = "action" } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const page = Number(searchParams.get("page") || "1") || 1;

  const category = getCategoryBySlug(type);
  const categoryName = category?.name ?? "全部";

  const [genre, setGenre] = useState("全部");
  const [year, setYear] = useState("全部");
  const [region, setRegion] = useState("全部");
  const [sort, setSort] = useState<SortKey>("latest");
  const [showFilters, setShowFilters] = useState(false);

  usePageMeta({
    title: `${categoryName}电影 - 电影天堂最新电影分类`,
    description: `浏览电影天堂${categoryName}电影分类，发现最新高清${categoryName}大片与热门推荐。`,
    keywords: `电影天堂,${categoryName}电影,最新电影,影视资源,高清电影`,
  });

  const filtered = useMemo(() => {
    const base = movies.filter((movie) => movie.genres.includes(type));
    const list = filterMovies(base, { genre, year, region });
    return sortMovies(list, sort);
  }, [type, genre, year, region, sort]);

  const { items, totalPages, currentPage, total } = paginate(filtered, page, PAGE_SIZE);

  const updatePage = (next: number) => {
    const params = new URLSearchParams(searchParams);
    if (next <= 1) params.delete("page");
    else params.set("page", String(next));
    setSearchParams(params);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const resetFilters = () => {
    setGenre("全部");
    setYear("全部");
    setRegion("全部");
    setSort("latest");
    updatePage(1);
  };

  const chip = (active: boolean) =>
    `whitespace-nowrap rounded-full px-3 py-1.5 text-sm transition ${
      active
        ? "bg-accent-500 font-medium text-background-950"
        : "bg-background-100 text-foreground-300 ring-1 ring-background-200/70 hover:text-accent-400"
    }`;

  return (
    <div className="flex min-h-screen w-full flex-col bg-background-50">
      <SiteHeader />

      <main className="flex-1">
        <div className="mx-auto w-full max-w-[1280px] px-4 py-8 md:px-6 md:py-10">
          <nav className="mb-4 flex flex-wrap items-center gap-1 text-sm text-foreground-400" aria-label="面包屑">
            <Link to="/" className="hover:text-accent-400">首页</Link>
            <i className="ri-arrow-right-s-line" />
            <span>电影分类</span>
            <i className="ri-arrow-right-s-line" />
            <span className="text-foreground-100">{categoryName}</span>
          </nav>

          <div className="flex flex-wrap items-end justify-between gap-4">
            <div>
              <h1 className="text-2xl font-bold text-foreground-50 md:text-3xl">
                {categoryName}电影
              </h1>
              <p className="mt-2 max-w-2xl text-sm leading-relaxed text-foreground-400">
                {category?.desc ?? "浏览电影天堂最新电影分类。"} 共收录 {total} 部相关影片。
              </p>
            </div>
            <button
              type="button"
              onClick={() => setShowFilters((prev) => !prev)}
              className="flex items-center gap-2 whitespace-nowrap rounded-md border border-background-300/70 bg-background-100 px-4 py-2 text-sm text-foreground-100 lg:hidden"
            >
              <i className="ri-filter-3-line" />
              筛选与排序
            </button>
          </div>

          {/* 类型入口 */}
          <div className="mt-6 flex flex-wrap gap-2">
            {movieCategories.map((cat) => (
              <Link
                key={cat.slug}
                to={`/category/${cat.slug}`}
                className={chip(cat.slug === type)}
              >
                {cat.name}
              </Link>
            ))}
          </div>

          <div className={`${showFilters ? "block" : "hidden"} lg:block`}>
            <div className="mt-5 space-y-3 rounded-lg bg-background-100 p-4 ring-1 ring-background-200/70">
              <div className="flex flex-col gap-2 md:flex-row md:items-center">
                <span className="w-16 shrink-0 text-sm text-foreground-500">年代</span>
                <div className="flex flex-wrap gap-2">
                  {decades.map((item) => (
                    <button key={item} type="button" onClick={() => { setYear(item); updatePage(1); }} className={chip(year === item)}>
                      {item}
                    </button>
                  ))}
                </div>
              </div>
              <div className="flex flex-col gap-2 md:flex-row md:items-center">
                <span className="w-16 shrink-0 text-sm text-foreground-500">地区</span>
                <div className="flex flex-wrap gap-2">
                  {regions.map((item) => (
                    <button key={item} type="button" onClick={() => { setRegion(item); updatePage(1); }} className={chip(region === item)}>
                      {item}
                    </button>
                  ))}
                </div>
              </div>
              <div className="flex flex-col gap-2 md:flex-row md:items-center">
                <span className="w-16 shrink-0 text-sm text-foreground-500">排序</span>
                <div className="flex flex-wrap gap-2">
                  {sortOptions.map((item) => (
                    <button key={item.key} type="button" onClick={() => { setSort(item.key); updatePage(1); }} className={chip(sort === item.key)}>
                      {item.label}
                    </button>
                  ))}
                  <button
                    type="button"
                    onClick={resetFilters}
                    className="flex items-center gap-1 whitespace-nowrap rounded-full px-3 py-1.5 text-sm text-foreground-500 transition hover:text-accent-400"
                  >
                    <i className="ri-refresh-line" />
                    重置
                  </button>
                </div>
              </div>
            </div>
          </div>

          {items.length === 0 ? (
            <div className="mt-12 flex flex-col items-center justify-center rounded-lg bg-background-100 py-16 ring-1 ring-background-200/70">
              <i className="ri-emotion-sad-line text-5xl text-foreground-500" />
              <p className="mt-4 text-foreground-300">该筛选条件下暂无影片</p>
              <button
                type="button"
                onClick={resetFilters}
                className="mt-5 whitespace-nowrap rounded-md bg-accent-500 px-5 py-2 text-sm font-medium text-background-950 transition hover:bg-accent-400"
              >
                重置筛选条件
              </button>
            </div>
          ) : (
            <div className="mt-6 grid grid-cols-2 gap-3 sm:grid-cols-3 md:gap-4 lg:grid-cols-4">
              {items.map((movie) => (
                <MovieCard key={movie.id} movie={movie} showSynopsis />
              ))}
            </div>
          )}

          <Pagination currentPage={currentPage} totalPages={totalPages} onPageChange={updatePage} />
        </div>
      </main>

      <SiteFooter />
    </div>
  );
}